<template>
    <div class="escaner-camara">
        <video ref="video" muted playsinline autoplay></video>
    </div>
</template>

<script>
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType } from "@zxing/library";

// Formatos presentes en cat_producto_barra: EAN-13, UPC-A, EAN-8, UPC-E,
// ITF-14 y codigos internos alfanumericos (Code 128 / Code 39).
// Limitar los formatos acelera cada intento de lectura.
const FORMATOS_NATIVOS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];
const FORMATOS_ZXING = [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
];

// Pausa entre intentos de lectura (evita saturar el CPU de equipos modestos)
const INTERVALO_NATIVO_MS = 80;
const INTERVALO_ZXING_MS = 100;
// Pausa despues de una lectura exitosa (solo zxing)
const PAUSA_TRAS_LECTURA_MS = 300;
// Zoom leve para evitar el lente gran angular en telefonos con varias camaras
const ZOOM_OBJETIVO = 1.5;

/**
 * Escaner de codigos de barras con la camara.
 *
 * Usa el lector nativo del navegador (BarcodeDetector: Chrome en Android)
 * cuando esta disponible, porque es acelerado por el sistema y lee en
 * cualquier orientacion. Si no existe (iOS, escritorio), usa zxing con los
 * formatos limitados y TRY_HARDER para leer tambien codigos girados.
 *
 * Eventos: decode(texto), loaded({ motor }), error(mensaje)
 */
export default {
    name: "EscanerCamaraBarras",

    mounted() {
        this.activo = true;
        this.stream = null;
        this.lector = null;
        this.timer = null;
        this.iniciar();
    },

    beforeDestroy() {
        this.detener();
    },

    methods: {
        iniciar() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                this.$emit('error', 'Su navegador no admite el acceso a la camara.');
                return;
            }
            navigator.mediaDevices.getUserMedia({
                audio: false,
                video: {
                    facingMode: { ideal: 'environment' },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
            })
                .then((stream) => {
                    // Si el modal se cerro mientras se pedia el permiso
                    if (!this.activo) {
                        stream.getTracks().forEach(t => t.stop());
                        return null;
                    }
                    this.stream = stream;
                    this.aplicarAjustesCamara(stream);
                    return this.crearDetectorNativo();
                })
                .then((detector) => {
                    if (!this.activo || !this.stream) return;
                    if (detector) {
                        this.iniciarNativo(detector);
                    } else {
                        this.iniciarZxing();
                    }
                })
                .catch((e) => {
                    this.$emit('error', this.describirError(e));
                });
        },

        aplicarAjustesCamara(stream) {
            const track = stream.getVideoTracks()[0];
            if (!track || typeof track.getCapabilities !== 'function' || typeof track.applyConstraints !== 'function') {
                return;
            }
            try {
                const capacidades = track.getCapabilities();
                const ajustes = {};
                if (capacidades.focusMode && capacidades.focusMode.includes('continuous')) {
                    ajustes.focusMode = 'continuous';
                }
                if (capacidades.zoom) {
                    const zoom = Math.min(ZOOM_OBJETIVO, capacidades.zoom.max || ZOOM_OBJETIVO);
                    if (zoom >= (capacidades.zoom.min || 1)) {
                        ajustes.zoom = zoom;
                    }
                }
                if (Object.keys(ajustes).length > 0) {
                    track.applyConstraints({ advanced: [ajustes] }).catch(() => {
                        // El dispositivo no acepta el ajuste; se sigue con los valores por defecto
                    });
                }
            } catch (e) {
                // getCapabilities no disponible en este navegador
            }
        },

        crearDetectorNativo() {
            if (!('BarcodeDetector' in window)) {
                return Promise.resolve(null);
            }
            return window.BarcodeDetector.getSupportedFormats()
                .then((soportados) => {
                    const formats = FORMATOS_NATIVOS.filter(f => soportados.includes(f));
                    if (formats.length === 0) return null;
                    return new window.BarcodeDetector({ formats: formats });
                })
                .catch(() => null);
        },

        iniciarNativo(detector) {
            const video = this.$refs.video;
            video.srcObject = this.stream;
            const reproducir = video.play();
            if (reproducir && typeof reproducir.catch === 'function') {
                reproducir.catch(() => {});
            }
            this.$emit('loaded', { motor: 'nativo' });

            const paso = () => {
                if (!this.activo) return;
                if (video.readyState < 2) {
                    this.timer = setTimeout(paso, INTERVALO_NATIVO_MS);
                    return;
                }
                detector.detect(video)
                    .then((codigos) => {
                        if (this.activo && codigos.length > 0 && codigos[0].rawValue) {
                            this.$emit('decode', codigos[0].rawValue);
                        }
                    })
                    .catch(() => {
                        // Cuadro no disponible; se reintenta en el siguiente paso
                    })
                    .finally(() => {
                        if (this.activo) {
                            this.timer = setTimeout(paso, INTERVALO_NATIVO_MS);
                        }
                    });
            };
            paso();
        },

        iniciarZxing() {
            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, FORMATOS_ZXING);
            // TRY_HARDER: revisa mas filas y prueba la imagen girada 90 grados
            hints.set(DecodeHintType.TRY_HARDER, true);

            this.lector = new BrowserMultiFormatReader(hints, PAUSA_TRAS_LECTURA_MS);
            this.lector.timeBetweenDecodingAttempts = INTERVALO_ZXING_MS;
            this.lector.decodeFromStream(this.stream, this.$refs.video, (resultado) => {
                if (resultado && this.activo) {
                    this.$emit('decode', resultado.getText());
                }
            }).catch((e) => {
                this.$emit('error', this.describirError(e));
            });
            this.$emit('loaded', { motor: 'zxing' });
        },

        detener() {
            this.activo = false;
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            if (this.lector) {
                this.lector.reset();
                this.lector = null;
            }
            if (this.stream) {
                this.stream.getTracks().forEach(t => t.stop());
                this.stream = null;
            }
        },

        describirError(e) {
            const nombre = e && e.name;
            if (nombre === 'NotAllowedError' || nombre === 'SecurityError') {
                return 'Permiso de camara denegado. Habilitelo en la configuracion del navegador para este sitio.';
            }
            if (nombre === 'NotFoundError' || nombre === 'OverconstrainedError') {
                return 'No se encontro una camara disponible en el dispositivo.';
            }
            if (nombre === 'NotReadableError') {
                return 'La camara esta en uso por otra aplicacion. Cierrela e intente de nuevo.';
            }
            return 'No se pudo iniciar la camara.';
        },
    },
};
</script>

<style scoped>
.escaner-camara {
    width: 100%;
    height: 100%;
    min-height: 290px;
}

.escaner-camara video {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 290px;
    object-fit: cover;
    border-radius: 8px;
}
</style>
